import React from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WidgetCard from './WidgetCard';
import { formatServerDate, useWidgetData } from './useWidgetData';

interface RecentDoc {
  id: number;
  status: number;
  statusName: string;
  typeName: string;
  sender: string;
  recipient: string;
  viaWarehouse: string | null;
  /** null - сумма пока недоступна (сервис номенклатуры недоступен) */
  amount: number | null;
  createdAt: string;
}

interface RecentResponse {
  documents: RecentDoc[];
  limit: number;
}

const ACCENT = '#7c3aed';
const headCell = { fontSize: 12, fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', whiteSpace: 'nowrap' as const, py: 0.75 };
const cell = { fontSize: 13, color: '#0f172a', py: 0.75, whiteSpace: 'nowrap' as const };

/** Цвет чипа статуса: завершено - зеленый, ошибки/отказ - красный, в процессе - синий, остальное - серый */
const statusChipColors = (status: number) => {
  if ([3, 4, 10, 16].includes(status)) return { bgcolor: '#ecfdf5', color: '#047857' };
  if ([5, 11, 14].includes(status)) return { bgcolor: '#fef2f2', color: '#b91c1c' };
  if ([2, 6, 7, 12, 13, 15].includes(status)) return { bgcolor: '#eff6ff', color: '#1d4ed8' };
  return { bgcolor: '#f1f5f9', color: '#475569' };
};

/** Виджет «Последние документы» - документы раздела «Документы» (созданные и полученные). Право: home.documents.recent.view (+ .limit) */
const RecentDocumentsWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<RecentResponse>('/api/v2/home/documents/recent');
  const docs = data?.documents || [];

  return (
    <WidgetCard
      title="Последние документы"
      icon={<DescriptionOutlinedIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
    >
      {!loading && docs.length === 0 ? (
        <Typography sx={{ p: 2, fontSize: 13, color: '#94a3b8' }}>Документов нет</Typography>
      ) : (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCell, pl: 2 }}>№ документа</TableCell>
              <TableCell sx={headCell}>Отправитель</TableCell>
              <TableCell sx={headCell}>Получатель</TableCell>
              <TableCell sx={headCell}>Статус</TableCell>
              <TableCell sx={headCell} align="right">Сумма</TableCell>
              <TableCell sx={{ ...headCell, pr: 2 }}>Создан</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id} hover>
                <TableCell sx={{ ...cell, pl: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{d.id}</Typography>
                    {d.typeName === 'Возврат' && (
                      <Chip size="small" label="Возврат" sx={{ height: 18, fontSize: 11, bgcolor: '#fff7ed', color: '#c2410c' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={cell}>{d.sender || '-'}</TableCell>
                <TableCell sx={cell}>{d.recipient || '-'}</TableCell>
                <TableCell sx={cell}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip size="small" label={d.statusName} sx={{ height: 22, fontWeight: 500, ...statusChipColors(d.status) }} />
                    {d.viaWarehouse && (
                      <Typography component="span" sx={{ fontSize: 12, color: '#64748b' }}>→ {d.viaWarehouse}</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={cell} align="right">
                  {d.amount != null ? d.amount.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '-'}
                </TableCell>
                <TableCell sx={{ ...cell, pr: 2, color: '#64748b' }}>{formatServerDate(d.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

export default RecentDocumentsWidget;
